import { HistoryPositionCode, PositionCode } from "../interfaces/positionCode";
import { fetchWithAuth } from "./fetchWithAuth";

const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";
const API_URL = process.env.REACT_APP_API_URL || "";
const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

const apiBaseUrl =
  window.location.hostname === "localhost"
    ? API_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_API_URL
      : DEV_API_URL;

export const createPositionCode = async (
  newPositionCode: PositionCode,
): Promise<PositionCode> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  try {
    const response = await fetch(
      `${apiBaseUrl}/api/PositionCode/CreatePositionCode`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newPositionCode),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to create position code: ${response.statusText}`);
    }

    const createdPositionCode = await response.json();
    return createdPositionCode;
  } catch (error) {
    console.error("Error creating position code:", error);
    throw error;
  }
};

export const createPositionCodeAdmin = async (
  newPositionCode: PositionCode,
): Promise<PositionCode> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/PositionCode/CreatePositionCode`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newPositionCode),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to create position code: ${response.statusText}`);
    }

    const createdPositionCode = await response.json();
    return createdPositionCode;
  } catch (error) {
    console.error("Error creating position code:", error);
    throw error;
  }
};

export const getPositionCodes = async (
  clientID: number,
): Promise<PositionCode[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const positionCodesResponse = await fetch(
    `${apiBaseUrl}/api/PositionCode/GetPositionCodes/${clientID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!positionCodesResponse.ok) {
    throw new Error("Failed to fetch position codes");
  }

  const positionCodes = await positionCodesResponse.json();
  return positionCodes;
};

export const getServiceIDsForPositionCode = async (
  positionCodeID: number,
): Promise<number[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  try {
    const response = await fetch(
      `${apiBaseUrl}/api/PositionCode/GetServiceIDsForPositionCode/${positionCodeID}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch services for position code: ${response.statusText}`,
      );
    }

    const serviceIDs = await response.json();
    return serviceIDs;
  } catch (error) {
    console.error("Error fetching services for position code:", error);
    throw error;
  }
};

export const deletePositionCode = async (
  positionCodeID: number,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/PositionCode/Delete/${positionCodeID}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to delete position code. Server responded with: ${errorText}`,
      );
    }

    console.log(
      `Position code with ID ${positionCodeID} deleted successfully.`,
    );
  } catch (error) {
    console.error("Error deleting position code:", error);
    throw error;
  }
};

export const updatePositionCode = async (positionCode: PositionCode) => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  try {
    const response = await fetch(
      `${apiBaseUrl}/api/PositionCode/UpdatePositionCode`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(positionCode),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to update position code. Server responded with: ${errorText}`,
      );
    }
  } catch (error) {
    console.error("Error updating position code:", error);
    throw error;
  }
};

export const updatePositionCodeAdmin = async (positionCode: PositionCode) => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/PositionCode/UpdatePositionCode`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(positionCode),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to update position code. Server responded with: ${errorText}`,
      );
    }
  } catch (error) {
    console.error("Error updating position code:", error);
    throw error;
  }
};

export const updateServicesForPositionCode = async (
  positionCodeID: number,
  serviceIDs: number[],
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  try {
    const response = await fetch(
      `${apiBaseUrl}/api/PositionCode/UpdateServicesForPositionCode/${positionCodeID}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(serviceIDs),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to update services for position code. Server responded with: ${errorText}`,
      );
    }
  } catch (error) {
    console.error("Error updating services for position code:", error);
    throw error;
  }
};

export const updateServicesForPositionCodeAdmin = async (
  positionCodeID: number,
  serviceIDs: number[],
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/PositionCode/UpdateServicesForPositionCodeAdmin/${positionCodeID}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(serviceIDs),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to update services for position code. Server responded with: ${errorText}`,
      );
    }
  } catch (error) {
    console.error("Error updating services for position code:", error);
    throw error;
  }
};

export const conflictRules: Record<number, number[]> = {
  1: [2, 3],
  2: [1, 3],
  3: [1, 2],
  9: [23],
  23: [9],
};

export const getDefaultPositionCodes = async (): Promise<PositionCode[]> => {
  // Default position codes
  const DEFAULT_POSITION_CODES: PositionCode[] = [
    {
      positionCodeID: 1,
      clientID: 0,
      positionCodeTitle: "Pastoral Call",
      fcraCompliance: true,
      criminalLookbackYears: 7,
      referenceLookbackYears: 10,
      requiresCandidatePayment: false,
    },
    {
      positionCodeID: 2,
      clientID: 0,
      positionCodeTitle: "Significant Lay Staff",
      fcraCompliance: true,
      criminalLookbackYears: 7,
      referenceLookbackYears: 10,
      requiresCandidatePayment: false,
    },
    {
      positionCodeID: 3,
      clientID: 0,
      positionCodeTitle: "Regular Lay Staff",
      fcraCompliance: true,
      criminalLookbackYears: 7,
      referenceLookbackYears: 10,
      requiresCandidatePayment: false,
    },
    {
      positionCodeID: 4,
      clientID: 0,
      positionCodeTitle: "Volunteer - Cash Handling",
      fcraCompliance: true,
      criminalLookbackYears: 7,
      referenceLookbackYears: 10,
      requiresCandidatePayment: false,
    },
    {
      positionCodeID: 5,
      clientID: 0,
      positionCodeTitle: "Volunteer - Driver",
      fcraCompliance: true,
      criminalLookbackYears: 7,
      referenceLookbackYears: 10,
      requiresCandidatePayment: false,
    },
    {
      positionCodeID: 6,
      clientID: 0,
      positionCodeTitle: "Volunteer",
      fcraCompliance: true,
      criminalLookbackYears: 7,
      referenceLookbackYears: 10,
      requiresCandidatePayment: false,
    },
  ];

  return DEFAULT_POSITION_CODES;
};

export const getServiceIDsForDefaultPositionCode = async (
  positionCodeID: number,
): Promise<number[]> => {
  const mockPositionCodeServices: Record<number, number[]> = {
    1: [2, 4, 5, 16, 12, 9, 10, 11], // Pastoral Call
    2: [4, 5, 16, 12, 10, 11], // Significant Lay Staff
    3: [4, 17, 10, 11], // Regular Lay Staff
    4: [10, 11, 16], // Volunteer - Cash Handling
    5: [17, 10, 11, 12], // Volunteer - Driver
    6: [17, 10, 11], // Volunteer
  };

  const serviceIDs = mockPositionCodeServices[positionCodeID];
  if (!serviceIDs) {
    throw new Error(
      `No services found for position code ID: ${positionCodeID}`,
    );
  }

  return serviceIDs;
};

export const getUCCDefaultPositionCodes = async (): Promise<PositionCode[]> => {
  // Default UCC position codes
  const DEFAULT_UCC_POSITION_CODES: PositionCode[] = [
    {
      positionCodeID: 1,
      clientID: 0,
      positionCodeTitle: "Reserved for Clergy (Prepaid)",
      fcraCompliance: true,
      criminalLookbackYears: 7,
      referenceLookbackYears: 10,
      requiresCandidatePayment: true,
    },
    {
      positionCodeID: 2,
      clientID: 0,
      positionCodeTitle: "Significant Lay Staff",
      fcraCompliance: true,
      criminalLookbackYears: 7,
      referenceLookbackYears: 10,
      requiresCandidatePayment: false,
    },
    {
      positionCodeID: 3,
      clientID: 0,
      positionCodeTitle: "Regular Staff",
      fcraCompliance: true,
      criminalLookbackYears: 7,
      referenceLookbackYears: 10,
      requiresCandidatePayment: false,
    },
    {
      positionCodeID: 4,
      clientID: 0,
      positionCodeTitle: "Volunteer - Cash Handling",
      fcraCompliance: true,
      criminalLookbackYears: 7,
      referenceLookbackYears: 10,
      requiresCandidatePayment: false,
    },
    {
      positionCodeID: 5,
      clientID: 0,
      positionCodeTitle: "Volunteer - Driver",
      fcraCompliance: true,
      criminalLookbackYears: 7,
      referenceLookbackYears: 10,
      requiresCandidatePayment: false,
    },
    {
      positionCodeID: 6,
      clientID: 0,
      positionCodeTitle: "Volunteer",
      fcraCompliance: true,
      criminalLookbackYears: 7,
      referenceLookbackYears: 10,
      requiresCandidatePayment: false,
    },
  ];

  return DEFAULT_UCC_POSITION_CODES;
};

export const getServiceIDsForUCCDefaultPositionCode = async (
  positionCodeID: number,
): Promise<number[]> => {
  const mockPositionCodeServices: Record<number, number[]> = {
    1: [4, 5, 6, 17, 10, 11, 30, 31, 32], // Reserved for Clergy
    2: [4, 5, 17, 12, 10, 11], // Significant Lay Staff
    3: [4, 17, 10, 11], // Regular Staff
    4: [10, 11, 16], // Volunteer - Cash Handling
    5: [17, 10, 11, 12], // Volunteer - Driver
    6: [17, 10, 11], // Volunteer
  };

  const serviceIDs = mockPositionCodeServices[positionCodeID];
  if (!serviceIDs) {
    throw new Error(
      `No services found for position code ID: ${positionCodeID}`,
    );
  }

  return serviceIDs;
};

export const getUUADefaultPositionCodes = async (): Promise<PositionCode[]> => {
  // Default UUA position codes
  const DEFAULT_UUA_POSITION_CODES: PositionCode[] = [
    {
      positionCodeID: 1,
      clientID: 0,
      positionCodeTitle: "All Ministers",
      fcraCompliance: true,
      criminalLookbackYears: 7,
      referenceLookbackYears: 10,
      requiresCandidatePayment: false,
    },
    {
      positionCodeID: 2,
      clientID: 0,
      positionCodeTitle: "Lay Staff Positions",
      fcraCompliance: true,
      criminalLookbackYears: 7,
      referenceLookbackYears: 10,
      requiresCandidatePayment: false,
    },
    {
      positionCodeID: 3,
      clientID: 0,
      positionCodeTitle: "Volunteer Positions",
      fcraCompliance: true,
      criminalLookbackYears: 7,
      referenceLookbackYears: 10,
      requiresCandidatePayment: false,
    },
  ];

  return DEFAULT_UUA_POSITION_CODES;
};

export const getServiceIDsForUUADefaultPositionCode = async (
  positionCodeID: number,
): Promise<number[]> => {
  const mockPositionCodeServices: Record<number, number[]> = {
    1: [4, 5, 6, 17], // All Ministers
    2: [4, 5, 17], // Lay Staff Positions
    3: [4, 5, 17], // Volunteer Positions
  };

  const serviceIDs = mockPositionCodeServices[positionCodeID];
  if (!serviceIDs) {
    throw new Error(
      `No services found for position code ID: ${positionCodeID}`,
    );
  }

  return serviceIDs;
};

export const getPositionCodeHistory = async (
  clientID: number,
): Promise<HistoryPositionCode[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/PositionCode/GetPositionCodesHistory/${clientID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch additional contact history");
  }

  const data: HistoryPositionCode[] = await response.json();
  return data;
};

export const normalize = (v: any) => v ?? "";

export const positionCodeEquals = (a: PositionCode, b?: PositionCode) => {
  if (!b) return false;
  return (
    normalize(a.positionCodeTitle) === normalize(b.positionCodeTitle) &&
    !!a.fcraCompliance === !!b.fcraCompliance &&
    (a.criminalLookbackYears ?? null) === (b.criminalLookbackYears ?? null) &&
    (a.referenceLookbackYears ?? null) === (b.referenceLookbackYears ?? null) &&
    !!a.requiresCandidatePayment === !!b.requiresCandidatePayment
  );
};

export const setsEqual = (a?: Set<number>, b?: Set<number>) => {
  const A = a ?? new Set<number>();
  const B = b ?? new Set<number>();

  if (A.size !== B.size) return false;

  let equal = true;
  A.forEach((v) => {
    if (!B.has(v)) equal = false;
  });

  return equal;
};
